import { getTourDefinition } from 'util/tours/tourDefinitions'

const STATUS = {
    COMPLETED: 'completed',
    DISMISSED: 'dismissed',
    PAUSED: 'paused',
    RESET: 'reset'
}

// All tours share this single user setting - its value is a JSON object keyed by each tour's
// #settingKeyword() (eg. "tourGeneralV1") - so onboarding progress doesn't add one setting row
// per tour.
const SETTINGS_KEYWORD = 'tours'

// PAUSED never blocks a tour from showing again - only finishing or hitting "End tour"
// (COMPLETED/DISMISSED) does.
const TERMINAL_STATUSES = [STATUS.COMPLETED, STATUS.DISMISSED]

/**
 * Onboarding tours, built on driver.js. Defined in tourDefinitions.js.
 * tryStart, canUseTours and resync are the only public API - everything else is private.
 */
export default class Tours {

    // Demo accounts are excluded - they keep the legacy TourToolTip system instead (see
    // Layout.js/Home.js/Show.js), so the two never overlap.
    static canUseTours() {
        return !ISMOBILE &&
            !!Backbone.Model.user?.id &&
            ACL.checkRole(ACL.roles.TEACHER) &&
            !Backbone.Model.user.get('is_demo') &&
            !Backbone.Model.user.get('exam_product')
    }

    // Called on every fresh login (see Layout#onUserIsLoggedIn) to force the cache to match the
    // backend, even down to nothing - otherwise a server-side reset wouldn't show until a new tab.
    static resync() {
        const raw = Backbone.Model.user.getSetting(SETTINGS_KEYWORD)
        sessionStorage.setItem(Tours.#cacheKey(), JSON.stringify(raw ? JSON.parse(raw) : {}))
    }

    // The method page views call. Resumes `tourId` if it was paused mid-way - only that tour,
    // never a different one - otherwise starts it if allowed, unseen, and its prerequisites and
    // condition (if any) pass.
    static tryStart(tourId) {
        if (!Tours.canUseTours() || Tours.#activeInstance) {
            return
        }

        const resumeAtIndex = Tours.#getResumePoint(tourId)
        const isResuming = resumeAtIndex !== undefined

        if (!isResuming) {
            if (Tours.#hasSeen(tourId)) {
                return
            }

            const tour = getTourDefinition(tourId)

            if (tour.requires?.some(requiredId => !Tours.#hasStarted(requiredId))) {
                return
            }

            if (tour.condition && !tour.condition()) {
                return
            }
        }

        Tours.#startOnceModalIsClosed(tourId, isResuming ? { resumeFromOriginalIndex: resumeAtIndex } : {})
    }

    // The currently running driver.js instance, if any. Only one tour can run at a time.
    static #activeInstance

    // Includes the tour's version, so bumping it in tourDefinitions.js re-shows a reworked tour
    // to everyone. This is the key under which the tour's status is stored inside the "tours"
    // setting's JSON blob - camelCase per docs/user-settings.md, eg. "tourGeneralV1".
    static #settingKeyword(tourId) {
        const { version } = getTourDefinition(tourId)
        return `tour${tourId.charAt(0).toUpperCase()}${tourId.slice(1)}V${version}`
    }

    // Namespaced by user id so switching accounts in the same tab can't leak one account's tour
    // statuses into another's.
    static #cacheKey() {
        return `learnbeat:tours:${Backbone.Model.user?.id}`
    }

    // sessionStorage is the source of truth for the rest of this tab's session. On first read
    // it's seeded from the user's real setting, so history from an earlier session still counts.
    // After that, sessionStorage wins even if something reloads the settings collection mid-
    // session (eg. creating a class) before a #recordStatus() save has landed.
    static #getAllStatuses() {
        const cacheKey = Tours.#cacheKey()
        const cached = sessionStorage.getItem(cacheKey)

        if (cached !== null) {
            return JSON.parse(cached)
        }

        const raw = Backbone.Model.user.getSetting(SETTINGS_KEYWORD)
        const statuses = raw ? JSON.parse(raw) : {}
        sessionStorage.setItem(cacheKey, JSON.stringify(statuses))
        return statuses
    }

    static #getStatus(tourId) {
        return Tours.#getAllStatuses()[Tours.#settingKeyword(tourId)]
    }

    // A merely PAUSED tour reads as "not seen" - it can still trigger again on its own.
    static #hasSeen(tourId) {
        return TERMINAL_STATUSES.includes(Tours.#getStatus(tourId))
    }

    // Used by `requires` checks: a prerequisite only needs to have begun, not finished, so eg.
    // the Library tour isn't blocked just because the general tour is paused, not resumed yet.
    static #hasStarted(tourId) {
        return Tours.#hasSeen(tourId) ||
            Tours.#getStatus(tourId) === STATUS.PAUSED ||
            Tours.#getResumePoint(tourId) !== undefined
    }

    // Also pushes to the user's real setting, so it survives across sessions once the backend
    // recognizes the keyword.
    static #recordStatus(tourId, status) {
        const statuses = { ...Tours.#getAllStatuses(), [Tours.#settingKeyword(tourId)]: status }
        sessionStorage.setItem(Tours.#cacheKey(), JSON.stringify(statuses))
        Backbone.Model.user.addSetting(SETTINGS_KEYWORD, JSON.stringify(statuses))
    }

    // Same-tab only - an abandoned pause just never resumes, which is fine.
    static #resumePointKey(tourId) {
        return `learnbeat:tours:resume:${Backbone.Model.user?.id}:${tourId}`
    }

    static #getResumePoint(tourId) {
        const raw = sessionStorage.getItem(Tours.#resumePointKey(tourId))
        return raw === null ? undefined : parseInt(raw, 10)
    }

    static #setResumePoint(tourId, stepIndex) {
        sessionStorage.setItem(Tours.#resumePointKey(tourId), stepIndex)
    }

    static #clearResumePoint(tourId) {
        sessionStorage.removeItem(Tours.#resumePointKey(tourId))
    }

    // Starts a tour immediately, or waits for the currently-open modal to close first.
    static #startOnceModalIsClosed(tourId, options = {}) {
        const modal = Backbone.View.Components?.modal
        if (modal?.isOpen) {
            const pathWhenQueued = window.app.controller.activePath.join('/')
            modal.once('modalClosed', () => {
                if (window.app.controller.activePath.join('/') === pathWhenQueued) {
                    Tours.#startOnceModalIsClosed(tourId, options)
                }
            })
            return
        }

        if (options.resumeFromOriginalIndex !== undefined) {
            Tours.#clearResumePoint(tourId)
        }

        Tours.#start(tourId, options)
    }

    /**
     * Force-starts a tour, skipping steps before resumeFromOriginalIndex.
     *
     * @param {string} tourId
     * @param {Object} [options]
     * @param {boolean} [options.persist] Set to false to preview without writing a status.
     * @param {number} [options.resumeFromOriginalIndex] Index into the tour's full step list.
     * @returns {Promise<void>}
     */
    static #start(tourId, { persist = true, resumeFromOriginalIndex = 0 } = {}) {
        const tour = getTourDefinition(tourId)

        // originalIndices[filteredIndex] maps back to the step's position in the full list -
        // needed to compute an accurate resume point if this run itself pauses.
        const { steps, originalIndices } = tour.steps.reduce((acc, step, originalIndex) => {
            if (originalIndex >= resumeFromOriginalIndex && (!step.condition || step.condition())) {
                acc.steps.push({ element: step.element, popover: step.popover, data: step.data })
                acc.originalIndices.push(originalIndex)
            }
            return acc
        }, { steps: [], originalIndices: [] })

        if (steps.length === 0) {
            return Promise.resolve()
        }

        // driver.js, its base stylesheet, and our theme on top of it are only ever needed once a
        // tour actually starts, so all three load as one chunk instead of bulking up the main
        // bundle for teachers who never see a tour.
        return Promise.all([
            import(
                /* rspackChunkName: "tours-driverjs" */
                'driver.js'
            ),
            import(
                /* rspackChunkName: "tours-driverjs" */
                'driver.js/dist/driver.css'
            ),
            import(
                /* rspackChunkName: "tours-driverjs" */
                'styling/components/_driverjs.scss'
            )
        ]).then(([{ driver }]) => {

            let completedViaDoneButton = false

            // Set only by the "End tour" button below - the one deliberate way to
            // permanently opt out of a tour.
            let dismissedViaSkipButton = false

            // Set by onRouteChange for a `continueOnNavigate` step, so the tour resumes AFTER
            // that step instead of repeating it.
            let resumeAtOriginalIndexOverride

            const instance = driver({
                steps,

                // Themed in _driverjs.scss via ".driver-popover.learnbeat-tour", which beats
                // driver.css's own rule on specificity alone.
                popoverClass: 'learnbeat-tour',
                showProgress: steps.length > 1,
                progressText: window.i18n.gettext('{{current}} of {{total}}'),
                nextBtnText: window.i18n.gettext('Next'),
                doneBtnText: tour.doneBtnText ?? window.i18n.gettext('Done'),
                smoothScroll: true,
                stagePadding: 8,
                stageRadius: 12,
                // Only Next/Done, "End tour" below, or a `continueOnNavigate` CTA can leave a step.
                allowClose: false,
                showButtons: ['next'],
                // Mirrors $blue-black in src/styling/variables/_colors.scss - keep in sync.
                overlayColor: '#122d40',
                overlayOpacity: 0.6,
                // Safety net for an element rendering a beat late - condition() above already
                // does the real gating.
                waitForElement: 3000,
                skipMissingElement: true,
                disableActiveInteraction: !tour.allowActiveInteraction,
                onDoneClick: () => {
                    completedViaDoneButton = true
                    instance.destroy()
                },
                onPopoverRender: (popover) => {
                    const skipButton = document.createElement('button')
                    skipButton.type = 'button'
                    skipButton.className = 'learnbeat-tour-skip-btn'
                    skipButton.textContent = window.i18n.gettext('End tour')
                    skipButton.addEventListener('click', () => {
                        dismissedViaSkipButton = true
                        instance.destroy()
                    })
                    popover.footer.insertBefore(skipButton, popover.footer.firstChild)
                },
                // Runs after driver.js's own auto-focus, which would otherwise land on this
                // button (the first focusable element in the popover) instead of Next/Done.
                onHighlighted: () => {
                    instance.getState('popover')?.nextButton?.focus()
                },
                onDestroyed: (element, step, opts) => {
                    if (persist) {
                        // Without Done or "End tour", this only fires via onRouteChange - an
                        // expected part of the tour, so it pauses rather than counting as an exit.
                        let status = STATUS.PAUSED
                        if (completedViaDoneButton) {
                            status = STATUS.COMPLETED
                        } else if (dismissedViaSkipButton) {
                            status = STATUS.DISMISSED
                        }

                        Tours.#recordStatus(tourId, status)

                        if (status === STATUS.PAUSED) {
                            const activeIndex = opts.state?.activeIndex ?? 0
                            const currentOriginalIndex = originalIndices[activeIndex] ?? activeIndex
                            Tours.#setResumePoint(tourId, resumeAtOriginalIndexOverride ?? currentOriginalIndex)
                        } else {
                            // No stale resume point should survive a real finish.
                            Tours.#clearResumePoint(tourId)
                        }
                    }

                    Tours.#activeInstance = undefined
                    window.app?.router?.off('route', onRouteChange)
                }
            })

            // A same-page route redispatch can fire too (seen right as a modal closes) - only
            // an actual path change counts as navigating away.
            const pathWhenStarted = window.app.controller.activePath.join('/')
            const onRouteChange = () => {
                if (window.app.controller.activePath.join('/') === pathWhenStarted) {
                    window.app?.router?.once('route', onRouteChange)
                    return
                }

                if (instance.getActiveStep()?.data?.continueOnNavigate) {
                    const originalIndex = originalIndices[instance.getActiveIndex()]
                    resumeAtOriginalIndexOverride = originalIndex + 1
                }
                instance.destroy()
            }

            Tours.#activeInstance = instance
            window.app?.router?.once('route', onRouteChange)

            // Always 0: `steps` is already trimmed to start at the resume point above.
            instance.drive(0)
        })
    }
}
